import { useEffect, useState } from 'react';

/**
 * Download page — links to the Android APK that ships baked into the same
 * nginx image. Stable URL: /joltstream.apk. iOS gets a "no native build
 * yet" message and is pointed at "Add to home screen".
 *
 * We probe HEAD /joltstream.apk to show APK file size + a friendly empty
 * state if the build job hasn't landed yet (CI failure, fresh clone).
 */
export function DownloadPage() {
  const [apkBytes, setApkBytes] = useState<number | null>(null);
  const [probing, setProbing] = useState(true);

  useEffect(() => {
    // Detect three states: real APK / no APK (production 404) / SPA fallback
    // (dev server has no nginx rule, returns HTML for unknown paths). The
    // SPA-fallback case looks like 200 + text/html — treat it as missing.
    fetch('/joltstream.apk', { method: 'HEAD' })
      .then((r) => {
        const ct = r.headers.get('content-type') ?? '';
        if (!r.ok || ct.startsWith('text/html')) {
          setApkBytes(0);
          return;
        }
        const len = r.headers.get('content-length');
        setApkBytes(len ? Number(len) : -1);
      })
      .catch(() => setApkBytes(0))
      .finally(() => setProbing(false));
  }, []);

  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <div className="page prose">
      <h1>Download</h1>

      <h2>Android</h2>
      {probing && <p className="hint">Checking for the latest build…</p>}

      {!probing && apkBytes !== null && apkBytes > 0 && (
        <>
          <p>
            <a className="cta" href="/joltstream.apk" download>
              Download joltstream.apk
            </a>
            <span className="hint" style={{ marginLeft: '0.75rem' }}>
              {formatBytes(apkBytes)} · unsigned debug build
            </span>
          </p>
          <p className="hint">
            On first install you'll need to allow "install from unknown sources"
            for your browser. Open the APK from your downloads to install.
          </p>
        </>
      )}

      {!probing && apkBytes === 0 && (
        <div className="banner banner-warn">
          <strong>Not built yet.</strong> The APK is produced by CI on every
          push to <code>web/</code>. If you're seeing this immediately after a
          deploy, the build is probably still running — check back in a few
          minutes.
        </div>
      )}

      <h3>Or just use the browser</h3>
      <p>
        You don't have to install anything to contribute. On Android, opening
        joltstream in Chrome (or Edge / Firefox) gives you the same recorder
        the APK has. The trade-off:
      </p>
      <ul>
        <li>
          <strong>Browser tab</strong>: data collection runs while the tab is
          in the foreground with the screen on. If you switch to another app
          (maps, music, anything), Android pauses the page and{' '}
          <em>data collection pauses too</em>. Nothing gets lost or damaged —
          measurement just stops until you come back to the tab. Anything
          already queued uploads on its own.
        </li>
        <li>
          <strong>APK</strong>: runs in the background via a foreground
          service, so you can put the phone in your pocket / use maps / lock
          the screen without losing the trip. Recommended for serious
          contributors.
        </li>
      </ul>

      <h2>iOS</h2>
      {isIos ? (
        <p>
          This is the alpha (v1) — the native iOS app arrives in v2. For now,
          install the web app to your home screen:
        </p>
      ) : (
        <p>
          This is the alpha (v1) — the native iOS app arrives in v2. iPhone /
          iPad users can install the web app to the home screen from Safari:
        </p>
      )}
      <ol>
        <li>Open this site in <strong>Safari</strong> (not Chrome)</li>
        <li>Tap the Share button</li>
        <li>Scroll down and tap <strong>Add to Home Screen</strong></li>
      </ol>
      <p className="hint">
        iOS limitation: sensors stop when the tab loses focus. Keep the screen
        on and joltstream in the foreground while driving. See <a href="/about">About</a> for
        the full picture and v2 plans.
      </p>

      <h2>Share with a friend</h2>
      <p>
        Visit <a href="/share">/share</a> for a QR code, native share sheet,
        and NFC-tag writer (Android). The more drivers contributing, the more
        useful the dataset.
      </p>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 0) return 'size unknown';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
